import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import adminApi from "../api/adminApi";

const getErrorMessage = (err, fallback) => {
  const data = err?.data || err?.response?.data;
  if (data?.message) return data.message;
  if (Array.isArray(data?.errors) && data.errors[0]?.msg) return data.errors[0].msg;
  return fallback;
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
};

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const AdminCalLogin = () => {
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("adminToken"));
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [statsError, setStatsError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [resettingAiQuotaUserId, setResettingAiQuotaUserId] = useState("");
  const [filters, setFilters] = useState({
    filter: "all",
    startDate: todayInputValue(),
    endDate: todayInputValue(),
  });
  const [resetDialog, setResetDialog] = useState({
    open: false,
    user: null,
    password: "",
    confirmPassword: "",
    error: "",
  });

  const statCards = useMemo(() => {
    const summary = stats?.summary || {};
    const appDownloadClicks = summary.appDownloadClicks || {};
    return [
      { label: "All users", value: summary.totalUsers || 0 },
      { label: "Filtered users", value: summary.filteredUsers || 0 },
      { label: "Verified users", value: summary.verifiedUsers || 0 },
      { label: "Google users", value: summary.googleUsers || 0 },
      { label: "Dashboards", value: summary.totalDashboards || 0 },
      { label: "Sections", value: summary.totalSections || 0 },
      { label: "Recipes", value: summary.totalRecipes || 0 },
      { label: "AI used/hr", value: summary.aiRequestsLastHour || 0 },
      { label: "AI hourly limit", value: summary.aiLimitPerHour || 0 },
      { label: "Google Play clicks", value: appDownloadClicks.googlePlay || 0 },
      { label: "iOS clicks", value: appDownloadClicks.ios || 0 },
      { label: "App clicks", value: appDownloadClicks.total || 0 },
    ];
  }, [stats]);

  const loadStats = useCallback(async (options = {}) => {
    if (!localStorage.getItem("adminToken")) return;

    const silent = options.silent === true;
    if (!silent) setIsLoading(true);
    setStatsError("");
    try {
      const params =
        filters.filter === "custom"
          ? filters
          : { filter: filters.filter };
      const data = await adminApi.getStats(params);
      setStats(data);
      setLastRefreshedAt(new Date());
    } catch (err) {
      if (err?.status === 401) {
        localStorage.removeItem("adminToken");
        setAdminToken(null);
      }
      setStatsError(getErrorMessage(err, "Unable to load admin statistics."));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (adminToken) loadStats();
  }, [adminToken, loadStats]);

  useEffect(() => {
    if (!adminToken) return undefined;

    const interval = window.setInterval(() => {
      loadStats({ silent: true });
    }, 5000);

    return () => window.clearInterval(interval);
  }, [adminToken, loadStats]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");
    setIsLoading(true);

    try {
      const data = await adminApi.login(loginForm);
      localStorage.setItem("adminToken", data.token);
      setAdminToken(data.token);
      setSuccessMessage("Admin session started.");
    } catch (err) {
      setLoginError(getErrorMessage(err, "Admin login failed."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setAdminToken(null);
    setStats(null);
  };

  const openResetDialog = (user) => {
    setResetDialog({
      open: true,
      user,
      password: "",
      confirmPassword: "",
      error: "",
    });
  };

  const closeResetDialog = () => {
    setResetDialog((prev) => ({ ...prev, open: false }));
  };

  const handleAdminResetPassword = async () => {
    if (!resetDialog.password || resetDialog.password.length < 8) {
      setResetDialog((prev) => ({
        ...prev,
        error: "Password must be at least 8 characters.",
      }));
      return;
    }

    if (resetDialog.password !== resetDialog.confirmPassword) {
      setResetDialog((prev) => ({
        ...prev,
        error: "Password and confirm password do not match.",
      }));
      return;
    }

    setIsLoading(true);
    try {
      const data = await adminApi.resetUserPassword(resetDialog.user._id, {
        password: resetDialog.password,
        confirmPassword: resetDialog.confirmPassword,
      });
      setSuccessMessage(data.message || "Password updated.");
      closeResetDialog();
      loadStats();
    } catch (err) {
      setResetDialog((prev) => ({
        ...prev,
        error: getErrorMessage(err, "Unable to reset password."),
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetAiQuota = async (user) => {
    setResettingAiQuotaUserId(user._id);
    setSuccessMessage("");
    setStatsError("");

    try {
      const data = await adminApi.resetUserAiQuota(user._id);
      setSuccessMessage(data.message || "AI quota reset.");
      await loadStats({ silent: true });
    } catch (err) {
      setStatsError(getErrorMessage(err, "Unable to reset AI quota."));
    } finally {
      setResettingAiQuotaUserId("");
    }
  };

  if (!adminToken) {
    return (
      <>
        <Helmet>
          <title>Admin Login | CaloriesPal</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <Box
          sx={{
            minHeight: "100vh",
            display: "grid",
            placeItems: "center",
            p: 2,
            background:
              "radial-gradient(circle at top left, rgba(34,197,94,0.22), transparent 35%), linear-gradient(135deg,#111827,#020617)",
          }}
        >
          <Paper
            component="form"
            onSubmit={handleLogin}
            elevation={0}
            sx={{
              width: "min(460px, 100%)",
              p: { xs: 3, sm: 4 },
              borderRadius: 4,
              border: "1px solid rgba(148,163,184,0.28)",
              boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
            }}
          >
            <Chip label="Private admin" color="success" sx={{ mb: 2 }} />
            <Typography variant="h4" fontWeight={800} gutterBottom>
              CaloriesPal Admin
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Sign in to review users, usage counts, and account support tools.
            </Typography>
            {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}
            <Stack spacing={2}>
              <TextField
                label="Admin username"
                value={loginForm.username}
                onChange={(event) =>
                  setLoginForm((prev) => ({ ...prev, username: event.target.value }))
                }
                fullWidth
                required
              />
              <TextField
                label="Admin password"
                type="password"
                value={loginForm.password}
                onChange={(event) =>
                  setLoginForm((prev) => ({ ...prev, password: event.target.value }))
                }
                fullWidth
                required
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ py: 1.4, borderRadius: 2, textTransform: "none" }}
              >
                {isLoading ? "Signing in..." : "Sign in to admin"}
              </Button>
            </Stack>
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard | CaloriesPal</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", p: { xs: 2, md: 4 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" fontWeight={800}>
              User Operations
            </Typography>
            <Typography color="text.secondary">
              Filter users by signup date and review account activity counts.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {lastRefreshedAt && (
              <Chip
                label={`Auto refresh: ${lastRefreshedAt.toLocaleTimeString()}`}
                variant="outlined"
              />
            )}
            <Button variant="outlined" onClick={loadStats} disabled={isLoading}>
              Refresh
            </Button>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Stack>
        </Stack>

        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage("")} sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        {statsError && <Alert severity="error" sx={{ mb: 2 }}>{statsError}</Alert>}

        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, mb: 3 }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", lg: "center" }}
          >
            <TextField
              select
              label="Created date filter"
              value={filters.filter}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, filter: event.target.value }))
              }
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="all">All time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="lastWeek">Last week</MenuItem>
              <MenuItem value="custom">Custom range</MenuItem>
            </TextField>
            {filters.filter === "custom" && (
              <>
                <TextField
                  label="Start date"
                  type="date"
                  value={filters.startDate}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, startDate: event.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End date"
                  type="date"
                  value={filters.endDate}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, endDate: event.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
            <Button variant="contained" onClick={loadStats} disabled={isLoading}>
              Apply filter
            </Button>
            {stats?.range?.label && (
              <Chip label={stats.range.label} sx={{ width: "fit-content" }} />
            )}
          </Stack>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((card) => (
            <Grid item xs={6} md={3} lg={2} key={card.label}>
              <Paper elevation={0} sx={{ p: 2.2, borderRadius: 3, height: "100%" }}>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography variant="h4" fontWeight={800}>
                  {card.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={800}>
              Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dashboard count is based on recipe categories/boards owned by each user.
            </Typography>
          </Box>
          <Divider />
          <Table sx={{ minWidth: 1180 }}>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>AI questions</TableCell>
                <TableCell>AI quota</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Recipes</TableCell>
                <TableCell align="right">Dashboards</TableCell>
                <TableCell align="right">Sections</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(stats?.users || []).map((user) => (
                <TableRow key={user._id} hover>
                  <TableCell>
                    <Typography fontWeight={700}>{user.username}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.name || "No display name"}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell sx={{ maxWidth: 340 }}>
                    {user.aiQuestions?.length ? (
                      <Stack spacing={0.75}>
                        {user.aiQuestions.map((question, index) => (
                          <Typography
                            key={`${user._id}-ai-${index}`}
                            variant="caption"
                            sx={{
                              display: "block",
                              color: "text.secondary",
                              lineHeight: 1.35,
                            }}
                          >
                            {index + 1}. {question.prompt}
                          </Typography>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No AI questions yet
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 190 }}>
                    <Stack spacing={0.75}>
                      <Typography variant="caption" color="text.secondary">
                        {user.aiQuota?.disabled
                          ? "Limit disabled"
                          : `${user.aiQuota?.used || 0}/${user.aiQuota?.limit || 0} used this hour`}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={user.aiQuota?.disabled ? 0 : user.aiQuota?.percent || 0}
                        sx={{
                          height: 8,
                          borderRadius: 99,
                          bgcolor: "rgba(148,163,184,0.22)",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 99,
                            bgcolor:
                              (user.aiQuota?.percent || 0) >= 90
                                ? "#dc2626"
                                : "#4d9700",
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {user.aiQuota?.resetAt
                          ? `Resets ${formatDate(user.aiQuota.resetAt)}`
                          : "No active usage"}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        disabled={
                          resettingAiQuotaUserId === user._id ||
                          !user.aiQuota?.used
                        }
                        onClick={() => handleResetAiQuota(user)}
                        sx={{ width: "fit-content", textTransform: "none" }}
                      >
                        {resettingAiQuotaUserId === user._id
                          ? "Resetting..."
                          : "Reset AI quota"}
                      </Button>
                    </Stack>
                  </TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell align="right">{user.recipes}</TableCell>
                  <TableCell align="right">{user.dashboards}</TableCell>
                  <TableCell align="right">{user.sections}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        size="small"
                        label={user.isVerified ? "Verified" : "Pending"}
                        color={user.isVerified ? "success" : "warning"}
                      />
                      <Chip size="small" label={user.authProvider || "local"} />
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openResetDialog(user)}>
                      Reset password
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!stats?.users?.length && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                    No users found for this filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={resetDialog.open}
          onClose={closeResetDialog}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Reset user password</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set a new password for {resetDialog.user?.username}.
            </Typography>
            {resetDialog.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {resetDialog.error}
              </Alert>
            )}
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="New password"
                type="password"
                value={resetDialog.password}
                onChange={(event) =>
                  setResetDialog((prev) => ({
                    ...prev,
                    password: event.target.value,
                    error: "",
                  }))
                }
                fullWidth
              />
              <TextField
                label="Confirm new password"
                type="password"
                value={resetDialog.confirmPassword}
                onChange={(event) =>
                  setResetDialog((prev) => ({
                    ...prev,
                    confirmPassword: event.target.value,
                    error: "",
                  }))
                }
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeResetDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAdminResetPassword}
              disabled={isLoading}
            >
              Reset password
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default AdminCalLogin;
